import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/** Page sizes offered across report and demand tables */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/** Default rows per page for the Open Demand tables */
export const DEFAULT_PAGE_SIZE = 10;

/**
 * The controls stay hidden while everything fits on a single default-sized
 * page, and appear as soon as the table holds more rows than a page can show.
 */
export function shouldShowPagination(totalItems: number, pageSize: number): boolean {
  return totalItems > DEFAULT_PAGE_SIZE || totalItems > pageSize;
}

/** Slices rows for the given page, clamping the page into range. */
export function paginate<T>(rows: T[], page: number, pageSize: number): T[] {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  return rows.slice((safePage - 1) * pageSize, safePage * pageSize);
}

interface TablePaginationProps {
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Plural noun used in the "Showing 1–25 of 40 …" summary */
  itemLabel: string;
  /** Keep the controls visible even when every row fits on one page */
  alwaysShow?: boolean;
}

export function TablePagination({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  itemLabel,
  alwaysShow = false,
}: TablePaginationProps) {
  if (!alwaysShow && !shouldShowPagination(totalItems, pageSize)) return null;

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 shrink-0 pb-2">
      <p className="text-[13px] text-muted-foreground whitespace-nowrap">
        Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)}–
        {Math.min(currentPage * pageSize, totalItems)} of {totalItems} {itemLabel}
      </p>

      <div className="flex items-center gap-2 shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="h-8 text-[13px]"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Previous
        </Button>
        <span className="text-[13px] text-muted-foreground px-1 whitespace-nowrap">
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className="h-8 text-[13px]"
        >
          Next
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[13px] text-muted-foreground whitespace-nowrap">Rows per page:</span>
        <Select
          value={String(pageSize)}
          onValueChange={v => { onPageSizeChange(Number(v)); onPageChange(1); }}
        >
          <SelectTrigger className="h-8 w-20 text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map(size => (
              <SelectItem key={size} value={String(size)}>{size}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
